import { useState } from 'react';
import { AlertTriangle, CheckCircle2, CircleDashed, Link2,
  Link2Off, TrendingDown } from 'lucide-react';
import { cn } from '../../lib/cn';
import { formatMoney, formatPercent } from '../../lib/format';
import type { MetricsBasis, AgreementMetrics, AgreementType } from './api';
import type { VerdictTone } from './verdict';

// ---------------------------------------------------------------------------
// Type badge
// ---------------------------------------------------------------------------

const TYPE_STYLES: Record<AgreementType, { label: string; className: string }> = {
  PROJECT: { label: 'Agreement', className: 'bg-accent/10 text-accent-700' },
  SUPPORT: { label: 'Support', className: 'bg-status-paid-bg text-status-paid' },
  RETAINER: { label: 'Retainer', className: 'bg-secondary/10 text-secondary' },
  HOSTING: { label: 'Hosting', className: 'bg-status-partial-bg text-status-partial' },
  INTERNAL: { label: 'Internal', className: 'bg-grey-100 text-grey-600' },
};

export function TypeBadge({ type, className }: { type: AgreementType; className?: string }) {
  const style = TYPE_STYLES[type] ?? TYPE_STYLES.PROJECT;
  return (
    <span
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold',
        style.className,
        className,
      )}
    >
      {style.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Hub badge — "the hub knows about this agreement too"
// ---------------------------------------------------------------------------

/**
 * Marks an agreement linked to a hub project (`Agreement.hubProjectId`). Deliberately
 * glyph-first: it rides inside a name cell that is already capped at 14rem, and
 * `Link2` means "linked" everywhere in this app (Settings → Hub, Settings →
 * Directory) — never anything else.
 */
export function HubBadge({ title, className }: { title?: string; className?: string }) {
  return (
    <span
      title={title ?? 'Linked to a hub project'}
      className={cn(
        'inline-flex shrink-0 items-center gap-1 rounded-full bg-accent/10 px-1.5 py-0.5 text-xs font-semibold text-accent-700',
        className,
      )}
    >
      <Link2 className="h-3 w-3" aria-hidden />
      <span className="sr-only sm:not-sr-only">Hub</span>
    </span>
  );
}

/**
 * The inverse of `HubBadge`, shown ONLY when the org policy requires linking:
 * a live agreement with no hub project behind it is unfinished, and the row
 * should say so before anyone opens it. Same glyph vocabulary — `Link2Off`
 * means "link missing" here and nowhere else.
 */
export function UnlinkedBadge({ className }: { className?: string }) {
  return (
    <span
      title="Not linked to a hub project — required by this organization"
      className={cn(
        'inline-flex shrink-0 items-center gap-1 rounded-full bg-status-late-bg px-1.5 py-0.5 text-xs font-semibold text-status-late',
        className,
      )}
    >
      <Link2Off className="h-3 w-3" aria-hidden />
      <span className="sr-only sm:not-sr-only">No hub</span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Verdict chip — the one-line arbitration between contradictory numbers.
// (The sentence itself is composed in verdict.ts; this only paints it.)
// ---------------------------------------------------------------------------

const VERDICT_STYLES: Record<VerdictTone, string> = {
  good: 'bg-status-paid-bg text-status-paid',
  watch: 'bg-status-partial-bg text-status-partial',
  // Amber ring rather than a second amber fill: "at risk" must not look
  // identical to "watch" at a glance, and must not look like "losing money".
  risk: 'bg-status-partial-bg text-status-partial ring-1 ring-inset ring-status-partial/40',
  bad: 'bg-status-late-bg text-status-late',
  neutral: 'bg-grey-100 text-grey-600',
};

const VERDICT_ICON: Record<VerdictTone, typeof CheckCircle2> = {
  good: CheckCircle2,
  watch: AlertTriangle,
  risk: AlertTriangle,
  bad: TrendingDown,
  neutral: CircleDashed,
};

export function VerdictChip({
  tone,
  label,
  title,
  size = 'md',
  className,
}: {
  tone: VerdictTone;
  label: string;
  title?: string;
  /** `sm` is the table-row form — same vocabulary and colour, ~30% narrower. */
  size?: 'sm' | 'md';
  className?: string;
}) {
  const Icon = VERDICT_ICON[tone];
  const sm = size === 'sm';
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded-full font-bold uppercase',
        sm
          ? 'gap-1 px-2 py-0.5 text-[0.625rem] tracking-wide'
          : 'gap-1.5 px-2.5 py-1 text-caption tracking-wider',
        VERDICT_STYLES[tone],
        className,
      )}
    >
      <Icon className={cn('shrink-0', sm ? 'h-3 w-3' : 'h-3.5 w-3.5')} aria-hidden />
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Basis vocabulary — one place decides what an agreement's money is CALLED, so
// "billable value" and "unbilled" never appear on a fixed-fee agreement.
// ---------------------------------------------------------------------------

export const BASIS_LABEL: Record<MetricsBasis, string> = {
  HOURLY: 'Billed hourly',
  MONTHLY_FEE: 'Monthly fee',
  FIXED_FEE: 'Fixed fee',
  NON_BILLABLE: 'Non-billable',
};

/** Header for the money column on the agreements list, per row basis. */
export const REVENUE_NOUN: Record<MetricsBasis, string> = {
  HOURLY: 'billable value of tracked time',
  MONTHLY_FEE: 'invoiced against the monthly fee',
  FIXED_FEE: 'the agreed contract value — logged hours are cost, never revenue',
  NON_BILLABLE: 'internal — no revenue',
};

/**
 * The word printed UNDER a revenue figure saying which basis produced it.
 *
 * Three bases share one column, and a reader who takes a fixed-bid contract
 * value for hours-at-rate reads the margin beside it backwards. It is keyed on
 * `revenueSource` — the field the server ships so no screen has to infer it —
 * not on `basis`, because a fixed-bid agreement with no agreed price falls back
 * to what has been invoiced and must say so.
 *
 * It costs no row height: the Agreement cell beside it is already two lines.
 */
export const REVENUE_SOURCE_TAG: Record<AgreementMetrics['revenueSource'], string> = {
  BILLABLE_VALUE: 'hours × rate',
  CONTRACT: 'contract value',
  INVOICED: 'invoiced',
  NONE: '',
};

/** The same idea as a NOUN, for running text ("on $104,610 billable value"). */
export const REVENUE_SOURCE_NOUN: Record<AgreementMetrics['revenueSource'], string> = {
  BILLABLE_VALUE: 'billable value',
  CONTRACT: 'contract value',
  INVOICED: 'invoiced',
  NONE: 'revenue',
};

// ---------------------------------------------------------------------------
// The rollup's basis — a total across agreements blends up to three of them
// ---------------------------------------------------------------------------

/**
 * A ROLLUP OF THREE BASES SAYS SO.
 *
 * Every row on this screen names the basis behind its own figure
 * (`REVENUE_SOURCE_TAG`), but the total under them carried no word at all —
 * so "$1,017,959 client revenue" read as one substance when it is contract
 * value, hours × rate and invoiced added together.
 *
 * That got worse the day "revenue" was defined app-wide to mean CASH (Jesse,
 * 2026-08-13): the firm's revenue is $1,004,839 of money received, and an
 * unlabelled $1,017,959 sat one click away looking like a second, larger
 * answer to the same question. It is a different question — what the WORK is
 * worth — and the rollup now says which.
 *
 * Order is fixed (contract → hours × rate → invoiced) so the phrase is stable
 * whatever order the rows arrive in.
 */
const ROLLUP_SOURCE_ORDER = ['CONTRACT', 'BILLABLE_VALUE', 'INVOICED'] as const;

export type RevenueSource = AgreementMetrics['revenueSource'];

/** The bases present, in the fixed order — one ordering, screen and CSV. */
export function orderedRollupSources(sources: readonly RevenueSource[]): RevenueSource[] {
  return ROLLUP_SOURCE_ORDER.filter((s) => sources.includes(s));
}

/** "contract value + hours × rate + invoiced" — only the bases actually in it. */
export function rollupBasisTag(sources: readonly RevenueSource[]): string {
  return orderedRollupSources(sources)
    .map((s) => REVENUE_SOURCE_TAG[s])
    .join(' + ');
}

/**
 * The explanation behind the rollup figure — the body of the (i) beside every
 * total across a set of agreements.
 *
 * ⚠ **It used to be a `title` AND a full sentence printed under the total.**
 * Jesse, 2026-08-13, reading the real screen: *"don't need as much explanation
 * on agreements page … if this description needs to be available make it in (i)
 * icon info popups."* The SHORT qualifier stays on screen — a rollup that
 * blends three bases and is not the cash the dashboard calls revenue must say
 * so, or the next reader takes it for the firm's revenue — and the paragraph
 * moved in here. A label, not a lesson.
 */
/** What each basis MEANS, one clause each — the popover lists only those present. */
const ROLLUP_SOURCE_MEANING: Record<RevenueSource, string> = {
  CONTRACT: 'a fixed bid earns its contract value, whatever the hours came to',
  BILLABLE_VALUE: 'hourly work, at each person’s bill rate',
  INVOICED: 'a monthly fee is earned as it is billed',
  NONE: 'no revenue — internal or non-billable work',
};

export function RollupBasisInfo({ sources }: { sources: readonly RevenueSource[] }) {
  const present = orderedRollupSources(sources);
  return (
    <>
      <p>
        {present.length > 1
          ? `This total adds up ${present.length} different bases:`
          : 'This total is one basis:'}
      </p>
      <ul className="mt-1.5 space-y-1">
        {present.map((source) => (
          <li key={source}>
            <strong className="font-semibold">{REVENUE_SOURCE_TAG[source]}</strong> —{' '}
            {ROLLUP_SOURCE_MEANING[source]}
          </li>
        ))}
      </ul>
      <p className="mt-2">Every row above names which one it is.</p>
      <p className="mt-2">
        It values the WORK, not cash collected — the dashboard’s and the Profit &amp; Loss’s revenue
        counts money on the day it arrives, so they read lower by everything delivered or billed and
        not yet paid for.
      </p>
    </>
  );
}

/**
 * The SHORT qualifier that stays on screen beside the rollup.
 *
 * ⚠ **IT COUNTS THE BASES ACTUALLY IN THE TOTAL.** A hardcoded "3 bases"
 * printed under a footer blending only two (the default /agreements view has no
 * active fixed-bid agreement, so CONTRACT is absent) — a screen that miscounts
 * its own contents is worse than one that says nothing.
 */
export function rollupBasisNote(sources: readonly RevenueSource[]): string {
  const n = orderedRollupSources(sources).length;
  return n > 1 ? `${n} bases · value of work, not cash` : 'value of work, not cash';
}

// ---------------------------------------------------------------------------
// Margin figure
// ---------------------------------------------------------------------------

/**
 * Margin bands.
 *
 * Margin is the most important number on the agreements screen and it used to
 * carry no visual weight at all: 21.9% and 70.5% rendered in identical
 * body-text near-black while the (period-scoped) burn bar carried all the
 * colour. Encoding is deliberately four-way rather than "red = bad, everything
 * else = ink", but "normal" stays ink so the column is not a wall of green —
 * only the exceptional and the troubling are coloured.
 *
 * `thin` was 20%, which left a two-state ramp in practice: 21.9% (Marketing
 * Retainer) and 25.7% (Growth Retainer) printed in exactly the same plain ink
 * as 47.5%, so a thin-but-positive margin — the one that is quietly costing
 * money — could only be found by reading every row. An agency floor is nearer
 * 30% than 20%, so that is where the amber band starts.
 *
 * This constant is the SINGLE definition: the list colour, the agreement
 * verdict's "THIN MARGIN", the client verdict's thin-margin narrative and the
 * "reprice the fee / review rates" action all read it, so a figure can never be
 * amber on one screen and a clean bill of health on the next.
 */
export const MARGIN_BANDS = { thin: 0.3, strong: 0.5 } as const;

export type MarginBand = 'loss' | 'thin' | 'normal' | 'strong';

export function marginBand(margin: number): MarginBand {
  if (margin < 0) return 'loss';
  if (margin < MARGIN_BANDS.thin) return 'thin';
  if (margin < MARGIN_BANDS.strong) return 'normal';
  return 'strong';
}

export const MARGIN_BAND_CLASS: Record<MarginBand, string> = {
  loss: 'text-status-late',
  thin: 'text-status-partial',
  normal: 'text-secondary',
  strong: 'text-status-paid',
};

export const MARGIN_BAND_LABEL: Record<MarginBand, string> = {
  loss: 'Losing money — cost exceeds recognised revenue',
  thin: 'Thin — under 30% margin',
  normal: 'Healthy — 30–50% margin',
  strong: 'Strong — 50% margin or better',
};

/**
 * Negative margin is ALWAYS the same red, at every size. The size class goes on
 * a wrapper rather than through `cn()` — tailwind-merge treats `text-heading-2`
 * and `text-status-late` as the same `text-*` group and silently drops one,
 * which is why the header used to render −606.6% in near-black.
 *
 * Percentages are `fixed` so a right-aligned column reads 66.0 / 66.9 / 67.0 /
 * 67.2 with the decimal points in line, not 66 / 66.9 / 67 / 67.2.
 */
export function MarginText({
  margin,
  size = 'inherit',
}: {
  margin: number | null;
  size?: 'inherit' | 'heading';
}) {
  const band = margin != null ? marginBand(margin) : null;
  const inner =
    margin == null || band == null ? (
      <span className="text-grey-400" title="No revenue recognised — margin is undefined">
        —
      </span>
    ) : (
      <span
        className={cn('num font-semibold', MARGIN_BAND_CLASS[band])}
        title={MARGIN_BAND_LABEL[band]}
      >
        {/* One negative convention per row: the budget tile beside this one
            renders −$957 with a true minus, so a hyphen here would put two
            different minus glyphs in the same KPI strip. */}
        {formatPercent(margin, { decimals: 1, fixed: true }).replace(/^-/, '−')}
      </span>
    );
  return size === 'heading' ? <span className="text-heading-2">{inner}</span> : inner;
}

/** Remaining budget / over-budget figure — one negative convention: a minus sign. */
export function SignedMoney({ value, whole = true }: { value: number; whole?: boolean }) {
  const negative = value < 0;
  return (
    <span className={cn('num', negative && 'text-status-late')}>
      {negative ? '−' : ''}
      {formatMoney(Math.abs(value), { whole })}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Inline dollar-rate editor (Team tab, Tasks tab, task manager)
// ---------------------------------------------------------------------------

export function RateCell({
  value,
  placeholder,
  onSave,
  disabled,
  label,
}: {
  value: number | null;
  /** Resolved default shown when no override is set, e.g. "145" or "flat". */
  placeholder: string;
  onSave: (value: number | null) => void;
  disabled?: boolean;
  label: string;
}) {
  const [text, setText] = useState(value != null ? String(value) : '');
  const [focused, setFocused] = useState(false);

  // Keep in sync when server state changes and the field is not being edited.
  const serverText = value != null ? String(value) : '';
  if (!focused && text !== serverText) setText(serverText);

  const commit = () => {
    setFocused(false);
    const trimmed = text.trim();
    const next = trimmed === '' ? null : Number(trimmed);
    if (next !== null && (!Number.isFinite(next) || next < 0)) {
      setText(serverText);
      return;
    }
    if (next !== value) onSave(next);
  };

  return (
    <div className="relative inline-block">
      <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-grey-400">
        $
      </span>
      <input
        aria-label={label}
        title={value != null ? 'Override set on this agreement' : `Inherited: ${placeholder}`}
        inputMode="decimal"
        className={cn(
          'num w-24 rounded-lg border py-1.5 pl-6 pr-2 text-right text-sm transition-all',
          'focus:outline-none focus:ring-2 focus:border-accent focus:ring-accent/20',
          // A set override is solid; an inherited value is dashed + faded so the
          // two are never confused at a glance.
          value != null
            ? 'border-grey-300 bg-white font-semibold text-secondary'
            : 'border-dashed border-grey-300 bg-grey-50 text-grey-500',
          'disabled:cursor-not-allowed disabled:opacity-60',
        )}
        placeholder={placeholder}
        value={text}
        disabled={disabled}
        onFocus={() => setFocused(true)}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          if (e.key === 'Escape') {
            setText(serverText);
            (e.target as HTMLInputElement).blur();
          }
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// CSV export — one shared implementation for every screen (see lib/csv.ts)
// ---------------------------------------------------------------------------

export { downloadCsv } from '../../lib/csv';

